import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SPECIES } from "@/content";

const MONOGRAMS = {
  Perro: { l: "P", c: "#FF5722" },
  Gato: { l: "G", c: "#0284C7" },
  Caballo: { l: "C", c: "#FF5722" },
  Vaca: { l: "V", c: "#0284C7" },
  Cerdo: { l: "C", c: "#FF5722" },
  Oveja: { l: "O", c: "#0284C7" },
  Cabra: { l: "Ca", c: "#FF5722" },
  Conejo: { l: "Co", c: "#0284C7" },
  Ratón: { l: "R", c: "#FF5722" },
  Mono: { l: "M", c: "#0284C7" },
  "Cerdo vietnamita": { l: "CV", c: "#FF5722" },
};

export default function Species() {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => SPECIES.filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase())),
    [q]
  );

  return (
    <section id="especies" data-testid="species-section" className="relative py-20 lg:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead
            eyebrow="Un solo equipo, todos tus pacientes"
            title="11 especies preconfiguradas y 14 personalizables"
            sub="Desde perros y gatos hasta caballos y cerdos vietnamitas: elige la especie, carga el perfil y analiza."
          />
          <Reveal delay={0.15}>
            <label className="relative block w-full lg:w-72" data-testid="species-search-wrapper">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="species-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar especie…"
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-3.5 text-sm font-semibold text-brand-ink placeholder:text-slate-400 outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 transition"
              />
            </label>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filtered.map((s, i) => {
            const m = MONOGRAMS[s.name] || { l: s.name.charAt(0), c: "#FF5722" };
            return (
              <motion.div
                key={s.name}
                data-testid={`species-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -5 }}
                className="relative flex flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-brand-cloud px-4 py-8 text-center cursor-default"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-2 -bottom-5 font-display text-[5.5rem] font-extrabold leading-none opacity-[0.08] select-none"
                >
                  {m.l}
                </span>
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm"
                  style={{ color: m.c }}
                >
                  <span className="font-display text-lg font-extrabold">{m.l}</span>
                </span>
                <span className="font-display text-sm font-bold text-brand-ink">{s.name}</span>
              </motion.div>
            );
          })}
          {filtered.length === 0 && (
            <p className="col-span-full py-8 text-center text-slate-500 font-semibold" data-testid="species-empty-state">
              No encontramos “{q}”. El equipo permite crear perfiles personalizados para especies nuevas.
            </p>
          )}
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="species-custom-banner"
            className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-3xl bg-brand-ink text-white px-7 py-6 grain"
          >
            <div className="flex items-center gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-orange">
                <SlidersHorizontal className="h-5 w-5 text-white" />
              </span>
              <p className="font-display text-lg font-bold">
                +14 perfiles de especie personalizables según tu casuística.
              </p>
            </div>
            <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
              Preconfiguradas: 11
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
